/**
 * Download a file from a (presigned) URL to disk, reporting streaming progress.
 *
 * Streams the response body via a reader so onProgress can be called as bytes
 * arrive; falls back to a single buffered blob() when the body is not a readable
 * stream. Content-Length is a CORS-safelisted response header, so it is readable
 * on presigned S3 GET responses without an ExposeHeaders rule.
 *
 * @param {string} url
 * @param {{ filename?: string, onProgress?: (loaded: number, total: number) => void }} [options]
 * @returns {Promise<void>}
 */
export const downloadFileWithProgress = async (url, { filename, onProgress } = {}) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const lengthHeader = response.headers.get('content-length');
  const total = lengthHeader ? parseInt(lengthHeader, 10) : 0;
  const contentType = response.headers.get('content-type');

  let blob;
  if (response.body && typeof response.body.getReader === 'function') {
    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.length;
      if (typeof onProgress === 'function') onProgress(loaded, total);
    }
    blob = new Blob(chunks, contentType ? { type: contentType } : undefined);
  } else {
    blob = await response.blob();
    if (typeof onProgress === 'function') onProgress(blob.size, blob.size);
  }

  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
};
